import { useEffect, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { FileXls, ArrowClockwise } from "@phosphor-icons/react";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function FiscalReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiscal/report/monthly", { params: { year, month, group_by: "customer" } });
      setData(data);
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Carrega uma vez na montagem; ano/mês são aplicados pelo botão "Gerar".
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    if (!data || !data.by_group?.length) { toast.error("Nada para exportar"); return; }
    const header = ["Cliente", "Qtd", "Total"];
    const rows = data.by_group.map((g) => [g.label, g.count, String(g.total.toFixed(2)).replace(".", ",")]);
    rows.push(["TOTAL", data.count_authorized, String(data.total_authorized.toFixed(2)).replace(".", ",")]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `relatorio_nfe_${year}_${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="space-y-4">
      <div className="border border-black bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <div className="overline text-xs text-zinc-500 mb-1">Ano</div>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
            min={2020} max={2100} className="border border-black px-3 py-2 w-28 font-mono" data-testid="fiscal-report-year" />
        </div>
        <div>
          <div className="overline text-xs text-zinc-500 mb-1">Mês</div>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border border-black px-3 py-2" data-testid="fiscal-report-month">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-4 py-2 bg-black text-white text-sm font-bold flex items-center gap-2" data-testid="fiscal-report-generate">
          <ArrowClockwise size={16} weight="bold" /> Gerar
        </button>
        <button onClick={exportCsv} disabled={!data} className="px-4 py-2 border border-black text-sm flex items-center gap-2 disabled:opacity-40 ml-auto" data-testid="fiscal-report-export">
          <FileXls size={16} weight="bold" /> Exportar CSV
        </button>
      </div>

      {loading && <div className="text-center text-zinc-400 py-8">Carregando…</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card label="Notas autorizadas" value={data.count_authorized} testid="fiscal-report-count-auth" />
            <Card label="Notas canceladas" value={data.count_canceled} testid="fiscal-report-count-cancel" />
            <Card label="Total faturado" value={fmtBRL(data.total_authorized)} mono testid="fiscal-report-total" />
          </div>

          <div className="border border-black bg-white">
            <div className="border-b border-black px-4 py-3 flex items-center justify-between">
              <div className="font-bold">Por cliente</div>
              <div className="text-xs text-zinc-500 font-mono">{data.by_group?.length || 0} clientes</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b border-black">
                <tr>
                  <th className="p-3 text-left">Cliente</th>
                  <th className="p-3 text-right">Qtd</th>
                  <th className="p-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_group || []).map((g) => (
                  <tr key={g.label} className="border-b border-zinc-200" data-testid={`fiscal-report-row-${g.label}`}>
                    <td className="p-3">{g.label}</td>
                    <td className="p-3 text-right font-mono">{g.count}</td>
                    <td className="p-3 text-right font-mono">{fmtBRL(g.total)}</td>
                  </tr>
                ))}
                {(data.by_group || []).length === 0 && (
                  <tr><td colSpan={3} className="p-6 text-center text-zinc-400">Nenhuma NF-e autorizada no período</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, mono, testid }) {
  return (
    <div className="border border-black bg-white p-4" data-testid={testid}>
      <div className="overline text-xs text-zinc-500">{label}</div>
      <div className={`text-2xl font-black mt-1 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
